import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import {
  datedAssignments,
  formatTimeRange,
  hasQuickAddParse,
  hourLabel,
  minutesToClock,
  minutesToInputTime,
  parseQuickAdd,
  quickAddTitle,
  weekdayName,
  daysBetween,
  type ISODate,
  type LearningItem,
  type Task,
} from '@app/shared'

import { Link } from 'react-router-dom'

import { Card, EmptyLine, SkeletonRows, useCardSurfaceLink } from '../Card'
import { CountRoll } from '../motion/CountRoll'
import { taskFileAway } from '../../lib/motion'
import { ParseChip } from '../tasks/ParseChip'
import { Bell, Calendar, Clock, GraduationCap, OpenBook } from '../icons'
import { axisHours, layoutClassTrack, trackFraction, trackWindow } from './track'
import { elapsedFraction, timeRemaining, untilClassPhrase, type TimeTone } from '../../lib/time'
import {
  isDemoMode,
  useAssignments,
  useClassOccurrences,
  useCreateTask,
  useLearningItems,
  useLearningStreak,
  useLearningTracks,
  useReminders,
  useToggleLearningItem,
  useToggleTask,
} from '../../lib/data'

/**
 * The four context cards of option 3c, in a 2x2 grid around the composer. All of it is
 * READ-ONLY SUMMARY (README §1): acting on an item belongs on its own screen.
 *
 * A card whose endpoint does not exist yet has to SAY so. An empty list is a claim — "no classes
 * today", "nothing due" — and making that claim because the data never arrived is the sort of lie
 * that gets a deadline missed. remote.ts throws a NotImplementedError carrying the version that
 * will fill the card in; this prints that sentence.
 */
function Unavailable({ error }: { error: unknown }) {
  if (!error) return null
  return <EmptyLine>{error instanceof Error ? error.message : 'This could not be loaded.'}</EmptyLine>
}

const TONE_TEXT: Record<TimeTone, string> = {
  overdue: 'text-danger-2',
  urgent: 'text-urgent-time',
  calm: 'text-microcopy',
}

// ── Due soon ────────────────────────────────────────────────────────────────

export function DueSoonCard({ today, nowMinutes }: { today: ISODate; nowMinutes: number }) {
  // openOnly = upcoming or missing: the next deadlines INCLUDING anything overdue, with
  // everything already handed in left out. Submitted work is not a deadline.
  const { data: assignments, isPending, error } = useAssignments({ openOnly: true, limit: 3 })

  // DUE SOON IS A COUNTDOWN, so it can only speak for work Canvas gave a deadline. Undated
  // assignments are real and appear on their course page; they have no place on a card whose
  // every row is a time remaining (packages/shared → datedAssignments).
  const rows = datedAssignments(assignments)
  const overdue = rows.filter((a) => timeRemaining(a.dueAt, today, nowMinutes).tone === 'overdue')
  const lead = rows[0]
  const rest = rows.slice(1)
  const leadTime = lead ? timeRemaining(lead.dueAt, today, nowMinutes) : null

  return (
    <Card
      to="/school"
      hue="var(--color-school)"
      linkLabel="Due soon — open School"
      className="gap-[14px]"
      // The card's own border warms when something is overdue — the one place on this screen
      // where a card frame carries state.
      border={overdue.length > 0 ? 'border-line-warm' : 'border-line'}
      icon={<Clock size={18} strokeWidth={1.8} className="text-danger-dot" />}
      title={
        <span className="text-[12px] font-bold tracking-[0.05em] text-danger-2">DUE SOON</span>
      }
      action={
        overdue.length > 0 ? (
          <span className="rounded-pill bg-danger-line-2 px-[9px] py-[3px] text-[10px] font-bold leading-[1.5] tracking-[0.04em] text-danger-dot">
            {overdue.length} OVERDUE
          </span>
        ) : null
      }
    >
      {isPending && <SkeletonRows rows={3} />}
      <Unavailable error={error} />
      {assignments?.length === 0 && <EmptyLine>Nothing due. Genuinely.</EmptyLine>}

      {lead && leadTime && (
        <div className="flex items-end gap-[14px]">
          <span
            className={[
              'text-[46px] font-light leading-none tracking-[-0.03em]',
              TONE_TEXT[leadTime.tone],
            ].join(' ')}
          >
            {leadTime.label}
          </span>
          <span className="min-w-0 pb-[6px]">
            <span className="block text-[15px] font-semibold leading-[1.2] text-pretty">
              {lead.title}
            </span>
            <span
              className={[
                'mt-1 block text-[12px] font-medium leading-[1.4]',
                leadTime.tone === 'overdue' ? 'text-danger-dot' : 'text-microcopy',
              ].join(' ')}
            >
              {lead.courseCode}
              {leadTime.tone === 'overdue' ? ' · past due, still open' : ' · still open'}
            </span>
          </span>
        </div>
      )}

      {rest.length > 0 && (
        <div className="mt-[2px] flex flex-col gap-[10px]">
          {rest.map((a) => {
            const t = timeRemaining(a.dueAt, today, nowMinutes)
            const elapsed = elapsedFraction(a.assignedAt, a.dueAt, today, nowMinutes)
            return (
              <div key={a.id} className="flex items-center gap-3">
                <span
                  className={[
                    'w-[38px] flex-none text-[11px] font-bold leading-none',
                    TONE_TEXT[t.tone],
                  ].join(' ')}
                >
                  {t.label}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={[
                      'block truncate text-[13.5px] font-medium leading-[1.2]',
                      t.tone === 'calm' ? 'text-ink-2' : '',
                    ].join(' ')}
                  >
                    {a.title}
                    <span className="font-normal text-microcopy"> · {a.courseCode}</span>
                  </span>
                  {/* No assignedAt from Canvas means no bar. Never a guessed one. */}
                  {elapsed !== null && (
                    <span
                      className={[
                        'relative mt-[7px] block h-1 rounded-[3px]',
                        t.tone === 'urgent' ? 'bg-danger-line-2' : 'bg-line-4',
                      ].join(' ')}
                      title={`${Math.round(elapsed * 100)}% of the time since it was handed out has gone`}
                    >
                      <span
                        className={[
                          'absolute inset-y-0 left-0 rounded-[3px]',
                          t.tone === 'urgent' ? 'bg-orange-2' : 'bg-faintest',
                        ].join(' ')}
                        style={{ width: `${Math.round(elapsed * 100)}%` }}
                      />
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// ── Today at Berkeley ───────────────────────────────────────────────────────

const KIND_LABEL: Record<string, string> = {
  lecture: 'Lecture',
  discussion: 'Discussion',
  lab: 'Lab',
}

export function ClassesCard({ date, nowMinutes }: { date: ISODate; nowMinutes: number }) {
  const { data: classes, isPending, error } = useClassOccurrences(date)

  const rows = classes ?? []
  const endsAt = rows.length ? Math.max(...rows.map((c) => c.endMinutes)) : null

  // EVERY position on this track is a fraction, never a pixel — see components/today/track.ts
  // for the bug that rule exists to make impossible.
  const dayWindow = trackWindow(rows)
  const { placed, nowFraction } = layoutClassTrack(rows, nowMinutes, dayWindow)
  const marks = axisHours(dayWindow)

  return (
    <Card
      to="/calendar"
      hue="var(--color-calendar)"
      linkLabel="Today at Berkeley — open Calendar"
      className="gap-[14px]"
      icon={<GraduationCap size={18} strokeWidth={1.7} className="text-school" />}
      title={<span className="text-[13px] font-semibold text-ink">Today at Berkeley</span>}
      action={
        rows.length > 0 ? (
          <span className="text-[11px] font-medium leading-none text-muted">
            {rows.length} {rows.length === 1 ? 'event' : 'events'} · ends{' '}
            {minutesToClock(endsAt!)}
          </span>
        ) : null
      }
    >
      {isPending && <SkeletonRows rows={3} />}
      <Unavailable error={error} />
      {classes?.length === 0 && <EmptyLine>No classes today.</EmptyLine>}

      {rows.length > 0 && (
        <div className="flex flex-1 gap-[14px]">
          {/* The gutter is positioned by the SAME fractions as the track beside it. It used to be
              a `justify-between` column, which is how a label and a block came to disagree. */}
          <div className="relative min-h-[172px] w-[46px] flex-none text-[10.5px] font-semibold leading-none text-faint">
            {marks.map((minutes, m) => (
              <span
                key={minutes}
                className="absolute left-0"
                style={{
                  top: `${trackFraction(minutes, dayWindow) * 100}%`,
                  transform:
                    m === 0
                      ? undefined
                      : m === marks.length - 1
                        ? 'translateY(-100%)'
                        : 'translateY(-50%)',
                }}
              >
                {hourLabel(minutes / 60)}
              </span>
            ))}
          </div>
          <div className="relative min-h-[172px] flex-1 border-l border-line-4 pl-[14px]">
            {marks.slice(1, -1).map((minutes) => (
              <span
                key={minutes}
                className="absolute inset-x-0 h-px bg-line"
                style={{ top: `${trackFraction(minutes, dayWindow) * 100}%` }}
                aria-hidden
              />
            ))}

            {placed.map(({ row: c, topFraction, heightPx, isNext, index: i }) => (
              <div
                key={`${c.courseId}-${c.startMinutes}`}
                className={[
                  'absolute left-[14px] box-border min-w-0 overflow-hidden rounded-button border-l-[3px] px-[11px] py-2',
                  isNext ? 'border-school bg-info-fill' : 'border-faintest bg-surface-4',
                ].join(' ')}
                // Top is a FRACTION of the track (so it is right at any height); height is the
                // handoff's own 52/34 px (so a block is the size it was drawn). track.ts reserves
                // room for it at the track's floor, which is the conservative end of that trade.
                style={{
                  top: `${topFraction * 100}%`,
                  height: `${heightPx}px`,
                  right: i === 0 ? 0 : i === 1 ? '22%' : '38%',
                }}
              >
                {isNext ? (
                  <div className="truncate text-[12.5px] font-semibold leading-[1.1] text-info">
                    {c.courseCode} {KIND_LABEL[c.kind]}
                  </div>
                ) : (
                  // Time LEADS the line: on the narrowest rows (i=1,2 lose 22%/38% of the track
                  // to the stair-step layout above) it is the course code and location that run
                  // out of room first, so the exact time — the whole point of this row — is the
                  // part that has to survive truncation, not the part that gets clipped.
                  <div className="flex items-baseline gap-[6px]">
                    <span className="flex-none text-[10.5px] font-semibold leading-[1.1] tabular-nums text-ink-3">
                      {formatTimeRange(c.startMinutes, c.endMinutes)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12px] font-semibold leading-[1.1] text-ink-2">
                      {c.courseCode} {KIND_LABEL[c.kind]} · {c.location}
                    </span>
                  </div>
                )}
                {isNext && (
                  <div className="mt-[3px] truncate text-[10.5px] leading-[1.2] text-school">
                    {formatTimeRange(c.startMinutes, c.endMinutes)} · {c.location} ·{' '}
                    {untilClassPhrase(c.startMinutes, c.endMinutes, nowMinutes)}
                  </div>
                )}
              </div>
            ))}

            {nowFraction !== null && (
              <div
                className="absolute -left-1 right-0 flex items-center gap-[6px]"
                style={{ top: `${nowFraction * 100}%` }}
                aria-hidden
              >
                <span className="size-[7px] flex-none rounded-full bg-danger-dot" />
                <span className="h-[1.5px] flex-1 bg-now-line" />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

// ── Learning (the one dark card in the bundle) ──────────────────────────────

export function LearningCard({ date }: { date: ISODate }) {
  const { data: items, isPending, error } = useLearningItems(date)
  const { data: streak } = useLearningStreak()
  const { data: tracks, isPending: tracksPending } = useLearningTracks()
  const toggle = useToggleLearningItem()

  // Zero curriculum is a real answer in remote mode — a plan is IMPORTED, never authored here —
  // and the demo fixture always carries two tracks, so this never shows there. Without this line
  // the card would just go quiet (a 0-streak, an empty checklist) and quiet reads as "nothing is
  // due today", which is a different and false claim from "no plan has been imported at all"
  // (README §State Management → hidden ≠ missing).
  const noCurriculum = !isDemoMode && !tracksPending && (tracks?.length ?? 0) === 0

  // The last 13 days, gaps included as zeroes — a heatmap that skips the empty days is a
  // heatmap that lies about the streak. Today is drawn as a dashed outline while anything on it
  // is still open, so the row never claims a finished day before it is finished.
  const byDate = new Map((streak?.days ?? []).map((d) => [d.date, d.minutes]))
  const openToday = (items ?? []).some((i) => !i.done)
  const bars = Array.from({ length: 13 }, (_, i) => {
    const d = shiftDate(date, i - 12)
    return { date: d, minutes: byDate.get(d) ?? 0, pending: d === date && openToday }
  })
  const peak = Math.max(1, ...bars.map((b) => b.minutes))

  // The same surface click the three white cards get. This one draws its own dark section rather
  // than taking the `Card` primitive, so it borrows the behaviour instead of inheriting it --
  // three cards that navigate and a fourth that looks identical and does nothing is worse than
  // none of them navigating (owner, 2026-09-01).
  const onSurfaceClick = useCardSurfaceLink('/learning')

  return (
    <section
      onClick={onSurfaceClick}
      className="mo-rule flex min-h-0 cursor-pointer flex-col gap-3 rounded-context bg-deep px-5 py-[18px] shadow-deep"
      style={{ '--mo-rule': 'var(--color-learning)' } as CSSProperties}
    >
      <header className="flex flex-none items-center gap-[9px]">
        <OpenBook size={18} strokeWidth={1.7} className="text-deep-5" />
        <h2 className="text-[12.5px] font-semibold leading-none text-on-dark">
          <Link
            to="/learning"
            className="rounded-[3px] hover:underline"
          >
            Learning
          </Link>
        </h2>
        {streak && streak.current > 0 && (
          <span className="ml-auto text-[11px] font-semibold leading-none text-deep-6">
            {streak.current}-day streak · best {streak.longest}
          </span>
        )}
      </header>

      {isPending && (
        <div className="h-[52px] animate-pulse rounded-tile bg-deep-line" aria-hidden />
      )}
      {error && (
        <p className="text-[11px] leading-[1.45] text-deep-6 text-pretty">
          {error instanceof Error ? error.message : 'Learning could not be loaded.'}
        </p>
      )}

      {streak && (
        <div className="flex items-end gap-[14px]">
          {/* 13b's tail, on the one number this card counts. Checking the day's last item off is
              what moves the streak, so THIS is the count that rolls up a digit when something is
              completed — the whole reward, exactly as the Done log's count is on Tasks. It rolls
              UP only: a streak that fell is a correction, and `CountRoll` swaps it in silence. */}
          <CountRoll
            value={streak.current}
            className="text-[44px] font-light leading-none tracking-[-0.03em] text-on-dark"
            lineHeight={44}
          />
          <div className="flex h-[52px] flex-1 items-end gap-[3px]" aria-hidden>
            {bars.map((b) => (
              <span
                key={b.date}
                className={[
                  'flex-1 rounded-[3px]',
                  b.pending
                    ? 'border-[1.5px] border-dashed border-deep-line'
                    : barFill(b.minutes / peak),
                ].join(' ')}
                style={{
                  // A day with no activity keeps a visible stub rather than disappearing: the
                  // gaps are what make the streak mean anything.
                  height: b.pending ? '30%' : `${Math.max(10, Math.round((b.minutes / peak) * 92))}%`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="h-px flex-none bg-deep-divider" aria-hidden />

      {noCurriculum && (
        <p className="text-[11px] leading-[1.45] text-deep-6 text-pretty">
          No curriculum imported yet — plans arrive through the Learning API.
        </p>
      )}

      <div className="flex flex-col gap-[9px]">
        {items?.map((item) => (
          <LearningChecklistRow key={item.id} item={item} onToggle={toggle.mutate} />
        ))}
      </div>
    </section>
  )
}

/**
 * ONE LINE OF TODAY'S CURRICULUM, AND 13b DONE HONESTLY ON A ROW THAT DOES NOT LEAVE.
 *
 * The brief's sequence is "box fills → checkmark pops → strikethrough wipes → label dims → the
 * row files itself away". The first four apply here verbatim. THE FIFTH DOES NOT, and pretending
 * otherwise would be a lie about the data: `getLearningItems(date)` returns the day's item
 * whether it is done or not (lib/data/demo.ts, and `/api/learning/items` behind it), so a checked
 * item STAYS on this card, struck through, as the record that the day's work happened. There is
 * no archive for it to travel toward, so it does not travel — `row: null` and the sequencer skips
 * that step. A collapse here would close a row that is still on screen the next second.
 *
 * THE WRITE STILL HAPPENS AT THE END, on `finished`, never on a frame: `runSequence`'s wall-clock
 * race is what guarantees the session is logged even in a tab that never composites.
 *
 * The colours are this card's own, not `.mo-file-box`/`.mo-file-label`. Those two rules paint
 * `success-dot` green and `microcopy` grey, which are the light card's answers; this is the one
 * dark surface in the bundle and its done state is `deep-4` on `deep-6`. Same beat, same tokens
 * file, the card's own hues.
 */
function LearningChecklistRow({
  item,
  onToggle,
}: {
  item: LearningItem
  onToggle: (v: { id: string; done: boolean }) => void
}) {
  const [filing, setFiling] = useState(false)
  const check = useRef<SVGSVGElement>(null)
  const strike = useRef<HTMLSpanElement>(null)

  // Reads as done from the moment the box is ticked — the sequence IS the confirmation, and a row
  // that stayed unstruck for 700ms and then flipped would be answering "did it work" twice.
  const done = item.done || filing

  // The sequence runs in an effect, not the handler, because the checkmark and the strike rule do
  // not exist in the DOM until `filing` has rendered them (TaskRow.tsx says the same, for the
  // same reason): starting from the handler would animate two nulls.
  useEffect(() => {
    if (!filing) return
    const run = taskFileAway({
      // No collapse: this row stays. See the header comment.
      row: null,
      check: check.current,
      strike: strike.current,
      rowHeight: 0,
      padTop: 0,
      padBottom: 0,
      borderTop: 0,
      borderBottom: 0,
    })
    let abandoned = false
    void run.finished.then(() => {
      if (!abandoned) onToggle({ id: item.id, done: true })
    })
    return () => {
      abandoned = true
      run.cancel()
    }
    // `onToggle` is a react-query mutate identity that changes on every mutation state change;
    // including it would restart the sequence mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filing, item.id])

  // Hand-off, not a flicker: `filing` ends only once the read has come back saying the item is
  // done, so `done` above never drops back to false between the write and the refetch.
  useEffect(() => {
    if (item.done) setFiling(false)
  }, [item.done])

  return (
    <label className="flex cursor-pointer items-center gap-[11px]">
      <input
        type="checkbox"
        checked={done}
        onChange={(e) => {
          // Unchecking deletes the logged session, and a deletion does not get filed away with a
          // flourish — it is written straight through, the way it always was.
          if (!e.target.checked) {
            onToggle({ id: item.id, done: false })
            return
          }
          if (!filing) setFiling(true)
        }}
        className="peer sr-only"
      />
      <span
        className={[
          'flex size-[17px] flex-none items-center justify-center rounded-keycap',
          'transition-colors duration-[var(--dur-state)] ease-[var(--ease-press)]',
          done ? 'bg-deep-4' : 'border-[1.5px] border-deep-4',
        ].join(' ')}
        aria-hidden
      >
        {done && (
          <svg
            ref={check}
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={3.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={filing && !item.done ? 'text-deep opacity-0' : 'text-deep'}
          >
            <path d="M5 12.6l4.4 4.4L19 7.4" />
          </svg>
        )}
      </span>
      <span className="relative min-w-0 flex-1">
        <span
          className={[
            'block truncate text-[13px] leading-[1.3]',
            'transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
            done ? 'font-normal text-deep-6' : 'font-medium text-on-dark',
            // While the rule is wiping it draws the strike; once the write has landed the
            // settled `line-through` takes over. Both at once would be two lines.
            item.done ? 'line-through' : '',
          ].join(' ')}
        >
          {shortTrack(item.trackName)}: {item.title}
        </span>
        {/* A drawn rule that WIPES left-to-right, not a `line-through` that appears. The wipe is
            the direction of reading; the property flipping on is a state you missed. */}
        {filing && !item.done && (
          <span
            ref={strike}
            aria-hidden
            className="pointer-events-none absolute left-0 top-1/2 h-[1.2px] w-0 bg-deep-6"
          />
        )}
      </span>
      {/* Minutes are LOGGED time, so an unfinished item has none — and none is invented. */}
      {item.minutes !== null && (
        <span className="flex-none text-[11px] leading-none text-deep-muted">
          {item.minutes} min
        </span>
      )}
    </label>
  )
}

function barFill(share: number): string {
  if (share === 0) return 'bg-deep-line'
  if (share > 0.85) return 'bg-deep-5'
  if (share > 0.65) return 'bg-deep-4'
  if (share > 0.45) return 'bg-deep-3'
  if (share > 0.25) return 'bg-deep-2'
  return 'bg-deep-line'
}

/** 'Algebra → Calculus' → 'Algebra'. The card has 220px; the full plan name lives on its page. */
function shortTrack(name: string): string {
  return name.split(/\s*[→·]\s*| /)[0] ?? name
}

function shiftDate(date: ISODate, days: number): ISODate {
  const d = new Date(`${date}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

// ── Reminders ───────────────────────────────────────────────────────────────

/**
 * How many rows the card shows per group. The card is a read-only summary and claims no count,
 * so the rest live on the Reminders screen, which owns them. A recently-completed reminder always
 * keeps one of the SOMEDAY slots: ticking something off should not make it vanish, and "done
 * Sat" is information for a few days afterwards.
 */
const GROUP_ROWS = 2

/**
 * The hour a reminder fires when the owner picks a day and leaves the time box alone. A DEFAULT,
 * not a guess: it is printed under the fields before the row is written, so nothing about the
 * saved timestamp is news afterwards.
 */
const DEFAULT_REMINDER_TIME = '09:00'
const DEFAULT_REMINDER_LABEL = '9:00 AM'

/**
 * '15:30' → 930. The inverse of `minutesToInputTime`, and it exists for one reason: `<input
 * type="time">` hands its value back in 24-hour 'HH:MM', and this app prints 12-hour time
 * everywhere a person reads it (packages/shared/src/dates.ts, convention table). Echoing the
 * input's raw value into the sentence under it would make this the one line on the screen that
 * says "15:30". Unparseable input yields the 9:00 default rather than NaN.
 */
function inputTimeToMinutes(value: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(value)
  if (!m) return 9 * 60
  return Number(m[1]) * 60 + Number(m[2])
}

export function RemindersCard({ today }: { today: ISODate }) {
  const { data: reminders, isPending, error } = useReminders()
  const toggle = useToggleTask()
  const create = useCreateTask()
  const [draft, setDraft] = useState('')
  // CONTROLLED, and the undo contract survives it: nothing rewrites this value while the owner
  // types (there is no autosave on this field), so the browser's own undo stack is intact.
  // Submitting clears it deliberately — that is a reset, not a silent rewrite.
  const inputRef = useRef<HTMLInputElement>(null)
  // WHEN, OPTIONAL, AND OFF BY DEFAULT (owner, 2026-08-28). Shut, this form adds exactly what it
  // added yesterday: an undated, ambient row. Open, it writes a real timed reminder — and it has
  // to be TIMED, not merely dated, because "ambient" is not a mood here but this card's read:
  // `/api/tasks/reminders` returns `reminder_at is not null OR due is null`, so a row given only
  // a day would be written correctly and then vanish onto the Reminders board — the exact defect
  // interactions.smoke.test.tsx §3c exists to catch. The hour is therefore part of the write, and
  // the 9:00 default is DECLARED under the fields rather than slipped in silently.
  const [dating, setDating] = useState(false)
  const [manualDue, setManualDue] = useState('')
  const [manualTime, setManualTime] = useState('')
  /** Titles added from this card in this session, newest first. See `rank` below. */
  const [justAdded, setJustAdded] = useState<string[]>([])

  // THE WORDS CAN SET THE WHEN (2026-08-28). "call mom friday 3pm" fills both boxes below — and
  // this is the one composer in the app where a spoken time has somewhere honest to land, since
  // `reminderAt` is exactly what this card's read is filtered on. The rules are the same three
  // the board's quick-add obeys (components/tasks/QuickAdd.tsx): the typed line is NEVER
  // rewritten, the reading is shown on a chip before the write, and a box touched by hand stops
  // taking suggestions for the rest of this draft.
  //
  // NO `#list` HERE, deliberately: this card reads /api/tasks/reminders and does not know what
  // lists exist, so it cannot tell a real one from a typo — and inventing a list from a typo is
  // exactly the failure the "unknown tags stay in the title" rule exists to prevent. Tags file
  // rows on the Reminders board, which does know.
  const [dateTouched, setDateTouched] = useState(false)
  const [timeTouched, setTimeTouched] = useState(false)
  const [rejected, setRejected] = useState(false)
  const parse = useMemo(() => parseQuickAdd(draft, { today }), [draft, today])
  const reading = !rejected && hasQuickAddParse(parse) ? parse : null
  const proposedDue = dateTouched ? null : reading?.due ?? null
  const proposedMinutes = timeTouched ? null : reading?.minutes ?? null
  const due = dateTouched ? manualDue : proposedDue ?? manualDue
  const time =
    timeTouched || proposedMinutes === null ? manualTime : minutesToInputTime(proposedMinutes)
  const whenOpen = dating || proposedDue !== null || proposedMinutes !== null

  const rows = reminders ?? []
  // WHAT YOU JUST TYPED IS WHAT YOU WANT TO SEE. The API returns someday reminders in title
  // order, and the card only has room for a couple of them — so without this, adding "Email the
  // advisor" to a card already showing "Ask about office hours" writes the row correctly and
  // shows nothing, which is indistinguishable from the bug this card just had. Session-local and
  // deliberately not persisted: it is about the last thirty seconds, not about the data.
  const rank = (t: Task) => {
    const i = justAdded.indexOf(t.title)
    return i === -1 ? justAdded.length : i
  }
  // Derived, never stored: a reminder with a date is this week's business, one without is someday.
  // Both groups are ranked by `justAdded` for the same reason — the card holds two rows per group,
  // and a row typed ten seconds ago that sorts behind two older ones is indistinguishable from a
  // write that did not happen.
  const thisWeek = rows
    .filter((t) => t.due !== null && !t.done)
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, GROUP_ROWS)
  const somedayOpen = rows
    .filter((t) => t.due === null && !t.done)
    .sort((a, b) => rank(a) - rank(b))
  const somedayDone = rows.filter((t) => t.due === null && t.done)
  const someday = [
    ...somedayOpen.slice(0, GROUP_ROWS - Math.min(1, somedayDone.length)),
    ...somedayDone.slice(0, 1),
  ]

  function add() {
    // A WORD IS ONLY TAKEN OUT OF THE TITLE IF IT LANDED SOMEWHERE. The day goes when the parse
    // is what supplied it; the time goes only when there is also a day for it to hang on, since
    // `reminderAt` is built from both — a "3pm" with no day is stored nowhere, so it stays in
    // his sentence instead of being deleted into silence.
    const keptDate = proposedDue !== null
    const keptTime = proposedMinutes !== null && due !== ''
    const title = (
      reading
        ? quickAddTitle(
            draft,
            reading.spans.filter(
              (s) => (s.kind === 'date' && keptDate) || (s.kind === 'time' && keptTime),
            ),
          )
        : draft
    ).trim()
    if (!title) return
    // NO DUE DATE UNLESS ONE WAS ASKED FOR, and that is still the fix this card was built around.
    // It reads `/api/tasks/reminders`, whose filter is `reminder_at is not null OR due is null` —
    // so a task written with `due: today` and nothing else (which is what this used to send) was
    // saved correctly and then landed on the Reminders board instead of in the card it was typed
    // into. Shut, the picker sends neither field and the row is ambient exactly as before.
    //
    // OPEN, IT SENDS BOTH. `due` is the day the row is grouped under; `reminderAt` is the instant,
    // built from the owner's own wall clock, and it is what keeps the row inside this card. The
    // hour falls back to 09:00 when the time box is left blank — a stated default, printed under
    // the fields, not a guess the app keeps to itself.
    const at = due ? new Date(`${due}T${time || DEFAULT_REMINDER_TIME}`) : null
    create.mutate({
      title,
      list: 'Inbox',
      ...(due && at && !Number.isNaN(at.getTime())
        ? { due: due as ISODate, reminderAt: at.toISOString() }
        : {}),
    })
    setJustAdded((previous) => [title, ...previous.filter((t) => t !== title)])
    setDraft('')
    setManualDue('')
    setManualTime('')
    setDating(false)
    setDateTouched(false)
    setTimeTouched(false)
    setRejected(false)
    // Focus stays where the owner put it: four reminders in a row is one thought, not four.
    inputRef.current?.focus()
  }

  return (
    <Card
      to="/tasks"
      hue="var(--color-tasks)"
      className="gap-[10px] py-[15px]"
      icon={<Bell size={18} strokeWidth={1.6} className="text-interests" />}
      title="Reminders"
      qualifier="NO DEADLINE · AMBIENT"
    >
      {isPending && <SkeletonRows rows={4} />}
      <Unavailable error={error} />
      {reminders?.length === 0 && <EmptyLine>Nothing set.</EmptyLine>}

      {/* KEYED BY id AND DONENESS, because 13b leaves a mark. The file-away collapses the row to
          0px and the animation holds it there (`fill: forwards`) — right, for a row that is
          leaving. An undated reminder does not leave: it comes back one slot down as "done Sat",
          and React would hand that new sentence the old, collapsed element. Re-keying gives it a
          fresh one, at full height, in its new place. */}
      <div className="flex flex-col gap-[7px]">
        {thisWeek.length > 0 && <GroupLabel>THIS WEEK</GroupLabel>}
        {thisWeek.map((t) => (
          <ReminderRow key={`${t.id}:${t.done}`} task={t} today={today} onToggle={toggle.mutate} />
        ))}

        {someday.length > 0 && (
          <>
            {thisWeek.length > 0 && <span className="my-[1px] h-px bg-fill" aria-hidden />}
            <GroupLabel>SOMEDAY</GroupLabel>
          </>
        )}
        {someday.map((t) => (
          <ReminderRow key={`${t.id}:${t.done}`} task={t} today={today} onToggle={toggle.mutate} />
        ))}
      </div>

      <form
        className="mt-auto flex flex-col gap-[6px]"
        onSubmit={(e) => {
          e.preventDefault()
          add()
        }}
      >
        <div className="flex items-center gap-[6px]">
          {/* 12d TEXT ENTRY (motion-brief §2): this is the "quick-add row" the brief names by
              name — hover fills, focus clears the fill and hands the edge to the ring. */}
          <div className="mo-field min-w-0 flex-1 rounded-button border border-dashed border-dash bg-surface-3">
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a reminder…"
              aria-label="Add a reminder"
              className="w-full border-0 bg-transparent px-3 py-2 text-[12.5px] leading-none text-ink-2 outline-none placeholder:text-microcopy"
            />
          </div>
          {/* Opening the when-row is its own act, so the default stays what the card says it is:
              NO DEADLINE · AMBIENT. Pressed, it is lit; pressing it again puts the row back to
              undated rather than leaving a hidden day attached to whatever gets typed next. */}
          <button
            type="button"
            onClick={() => {
              // Shutting the row empties BOTH boxes and, either way, counts as setting the when
              // by hand — so a recognised day cannot reopen the row the owner just closed.
              setDateTouched(true)
              setTimeTouched(true)
              setManualDue('')
              setManualTime('')
              setDating(!whenOpen)
            }}
            aria-pressed={whenOpen}
            aria-label={whenOpen ? 'Add it with no date' : 'Give it a date'}
            title={whenOpen ? 'Add it with no date' : 'Give it a date'}
            className={[
              'flex size-[30px] flex-none items-center justify-center rounded-button',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              whenOpen ? 'bg-fill-2 text-ink-2' : 'text-muted hover:bg-fill-2 hover:text-ink-2',
            ].join(' ')}
          >
            <Calendar size={15} />
          </button>
          {/* The owner asked for a button beside the field: Enter is not discoverable, and a form
              whose only affordance is a keystroke reads as a search box. Disabled on an empty
              draft, because a reminder with no words is not a reminder.

              A LIGHT INK ON A DARK GROUND (same reasoning as the calendar composer's Save): `Add`
              is `bg-ink`, so the wash has to come from the surface hue to read at all. Disabled
              stays a visible dark pill at reduced opacity rather than fading to text-on-nothing —
              `.mo-ink[disabled]` already strips the hover/press feedback, so `disabled:opacity-40`
              is the only signal left to carry "not yet". */}
          <button
            type="submit"
            disabled={draft.trim().length === 0}
            aria-label="Add reminder"
            style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
            className="mo-ink mo-press flex-none rounded-pill bg-ink px-[13px] py-[7px] text-[12.5px] font-semibold leading-none text-surface disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add
          </button>
        </div>

        {reading && (
          <ParseChip
            // Only what the parse still speaks for: a box set by hand has taken its field back.
            due={proposedDue}
            minutes={proposedMinutes}
            list={null}
            note={
              proposedMinutes !== null && !due
                ? 'a time needs a day — pick one, or this stays ambient'
                : undefined
            }
            onReject={() => setRejected(true)}
          />
        )}

        {whenOpen && (
          <div className="flex flex-col gap-[4px]">
            <div className="flex items-center gap-[6px]">
              <input
                type="date"
                value={due}
                onChange={(e) => {
                  setDateTouched(true)
                  setDating(true)
                  setManualDue(e.currentTarget.value)
                }}
                aria-label="Remind me on"
                className="min-w-0 flex-1 rounded-button border border-line bg-surface px-[9px] py-[6px] text-[12px] font-medium leading-none text-ink-2"
              />
              <input
                type="time"
                value={time}
                onChange={(e) => {
                  setTimeTouched(true)
                  setDating(true)
                  setManualTime(e.currentTarget.value)
                }}
                aria-label="Remind me at"
                className="min-w-0 flex-1 rounded-button border border-line bg-surface px-[9px] py-[6px] text-[12px] font-medium leading-none text-ink-2"
              />
            </div>
            <span className="text-[10px] leading-[1.4] text-faintest">
              {due
                ? `Reminds at ${time ? minutesToClock(inputTimeToMinutes(time)) : DEFAULT_REMINDER_LABEL} · shows under THIS WEEK`
                : `No day picked yet — leave it and this stays ambient. Blank time means ${DEFAULT_REMINDER_LABEL}.`}
            </span>
          </div>
        )}
      </form>
    </Card>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold leading-none tracking-[0.08em] text-faintest">
      {children}
    </span>
  )
}

/**
 * ONE AMBIENT REMINDER, AND 13b ON THE ROW THAT ACTUALLY LEAVES.
 *
 * Completing files the row away exactly the way a task row does (components/tasks/TaskRow.tsx):
 * the dot fills green, the strikethrough wipes left-to-right, the label dims, the row travels
 * 26px RIGHT and collapses — height, padding and border-width in the same stops, measured off the
 * row on screen — and only THEN is the completion written. A dated row leaves the card outright;
 * an undated one comes back one slot down as "done Sat", which is this card's own small archive.
 *
 * NO CHECKMARK POP, and that is a fact about the control rather than a shortcut. The toggle here
 * is the 6px dot the handoff draws, not the 18px box the Tasks board draws; an 11px checkmark
 * cannot go inside it, and growing the dot to make room would be motion scaling a control, which
 * §2 forbids outright. So `check` is null, the sequencer skips that step, and the green fill on
 * `.mo-file-box` carries "did it work" on its own.
 *
 * REOPENING IS NOT A CEREMONY. Un-ticking writes straight through: filing away is what completion
 * looks like, and running it backwards would animate an undo as if it were an achievement.
 */
function ReminderRow({
  task,
  today,
  onToggle,
}: {
  task: Task
  today: ISODate
  onToggle: (v: { id: string; done: boolean }) => void
}) {
  const aging = !task.done && task.due !== null && task.due <= today
  const ageDays = daysBetween(task.createdAt.slice(0, 10) as ISODate, today)

  const [filing, setFiling] = useState(false)
  const row = useRef<HTMLDivElement>(null)
  const strike = useRef<HTMLSpanElement>(null)

  // In an effect rather than the click handler: the strike rule does not exist in the DOM until
  // `filing` has rendered it, so a sequence started from the handler would animate a null.
  useEffect(() => {
    if (!filing) return
    const node = row.current
    if (!node) {
      onToggle({ id: task.id, done: true })
      return
    }
    const box = node.getBoundingClientRect()
    const style = getComputedStyle(node)
    // Measured, never assumed. This row's height depends on whether the title wrapped and on
    // which trailing chip it drew; a hard-coded number would collapse the wrong amount.
    const run = taskFileAway({
      row: node,
      check: null,
      strike: strike.current,
      rowHeight: box.height,
      padTop: parseFloat(style.paddingTop) || 0,
      padBottom: parseFloat(style.paddingBottom) || 0,
      borderTop: parseFloat(style.borderTopWidth) || 0,
      borderBottom: parseFloat(style.borderBottomWidth) || 0,
    })
    let abandoned = false
    // THE WRITE LANDS ON `finished`, which `runSequence` resolves off the wall clock rather than
    // off a composited frame — so a backgrounded tab still completes the reminder. It is also why
    // "Nothing set." cannot flash before the collapse ends: nothing has changed until here.
    void run.finished.then(() => {
      if (!abandoned) onToggle({ id: task.id, done: true })
    })
    return () => {
      abandoned = true
      run.cancel()
    }
    // `onToggle` is a react-query mutate identity that changes on every mutation state change;
    // including it would restart the sequence mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filing, task.id])

  return (
    // The hover lift the README specifies for rows, given a shape: the row itself is flush with
    // the card, so the fill is pulled out into the card's padding rather than boxing the text.
    <div
      ref={row}
      data-filing={filing || undefined}
      className={[
        '-mx-2 flex items-center gap-[10px] rounded-chip px-2 py-[1px]',
        'transition-[background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2',
        // Only while filing: the collapse animates `height`, and without this the row's contents
        // spill out of a box that is closing around them.
        filing ? 'overflow-hidden' : '',
      ].join(' ')}
    >
      <button
        type="button"
        aria-label={task.done ? `Reopen ${task.title}` : `Complete ${task.title}`}
        aria-pressed={task.done}
        onClick={() => {
          if (task.done) {
            onToggle({ id: task.id, done: false })
            return
          }
          if (!filing) setFiling(true)
        }}
        className={[
          'mo-file-box size-[6px] flex-none rounded-full transition-[background-color,opacity]',
          'duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-70',
          task.done ? 'bg-fill-3' : task.due !== null ? 'bg-interests' : 'bg-dash',
        ].join(' ')}
      />
      <span className="relative min-w-0 flex-1">
        <span
          className={[
            'mo-file-label block truncate text-[14px] leading-[1.4]',
            task.done
              ? 'font-normal text-microcopy line-through'
              : task.due !== null
                ? 'font-medium text-ink-2'
                : 'font-normal text-ink-3',
          ].join(' ')}
        >
          {task.title}
        </span>
        {/* A drawn rule that WIPES left-to-right, not a `line-through` that appears. */}
        {filing && (
          <span
            ref={strike}
            aria-hidden
            className="pointer-events-none absolute left-0 top-1/2 h-[1.2px] w-0 bg-microcopy"
          />
        )}
      </span>

      {task.done && task.completedAt ? (
        <span className="flex-none text-[11px] leading-none text-faintest">
          done {weekdayName(task.completedAt.slice(0, 10) as ISODate, true)}
        </span>
      ) : aging ? (
        // How long it has been sitting there. Nagging is not the point — knowing is.
        <span className="flex-none rounded-pill bg-accent-fill px-[7px] py-[2px] text-[10px] font-semibold leading-[1.5] text-accent-2">
          {ageDays} {ageDays === 1 ? 'day' : 'days'}
        </span>
      ) : task.due !== null ? (
        <span className="flex-none text-[11px] leading-none text-microcopy">
          before {weekdayName(task.due, true)}
        </span>
      ) : (
        <span className="flex-none text-[11px] leading-none text-faintest">{task.list}</span>
      )}
    </div>
  )
}
